/**
 * v3.15-1154 — Global Footer (public-page nav structure).
 * v3.21-1788 — Version badge now reads the canonical SYSTEM_VERSION from
 * `/version.json` (served by the Hub at build time) at mount, with a
 * safe fallback if the request fails, so the footer NEVER drifts from
 * the running release.
 * v3.21-1803 — Permanent "Install Hub App" footer control lives next
 * to the version badge.  The button is a permanent in-Hub entry point
 * for installing the PWA even when the browser's initial install
 * prompt was missed or dismissed.  It uses a shared
 * `usePwaInstall()` hook that captures `beforeinstallprompt` at
 * module load, hides itself when the app is already running as a
 * standalone PWA, and falls back to platform-specific manual install
 * instructions when a native deferred prompt is unavailable.
 * Internal/dashboard surface is unaffected — this footer only renders on
 * pages that explicitly include it (LoginPage + PublicLayout).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InstallHubAppButton from './InstallHubAppButton';

const COL_COMPANY = [
  { label: 'About',                 to: '/about'             },
  { label: 'Mission',               to: '/mission'           },
  { label: 'The Platform',          to: '/platform'          },
  { label: 'Inside Prestigious Hub',to: '/platform-tour'     },
  { label: 'Ghost Intelligence',    to: '/ghost'             },
  { label: 'Presentation',          to: '/presentation'      },
  { label: 'Application Concierge', to: '/concierge'         },
  { label: 'Partner Benefits',      to: '/partner-benefits'  },
  { label: 'FAQ',                   to: '/faq'               },
  { label: 'Contact', href: 'mailto:support@prestigiouscompanies.org' },
];
const COL_SOLUTIONS = [
  { label: 'EasyQuickFund (Funding)',          href: 'https://easyquickfund.com'   },
  { label: 'CreditForward360 (Credit Improvement)', href: 'https://creditforward360.com' },
  { label: 'The Platform',                     to:   '/platform'                   },
];
const COL_LEGAL = [
  { label: 'Privacy Policy',     to: '/privacy' },
  { label: 'Terms & Conditions', to: '/legal'   },
];

const FooterCol = ({ heading, items, testId }) => (
  <div data-testid={testId}>
    <h3 className="text-xs uppercase tracking-[0.15em] text-slate-400 mb-3 font-semibold">{heading}</h3>
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={it.label}>
          {it.to ? (
            <Link to={it.to} className="text-sm text-slate-300 hover:text-white transition-colors">
              {it.label}
            </Link>
          ) : (
            <a href={it.href} className="text-sm text-slate-300 hover:text-white transition-colors"
               target={it.href?.startsWith('http') ? '_blank' : undefined}
               rel={it.href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
              {it.label}
            </a>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  // v3.21-1788 — Canonical version fetched at mount from `/version.json`
  // (regenerated on every build).  If the fetch fails we display a
  // neutral "v" tag rather than a stale hardcoded string.
  const [version, setVersion] = useState('');
  useEffect(() => {
    let cancelled = false;
    fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : null)
      .then((v) => { if (!cancelled && v?.version) setVersion(String(v.version)); })
      .catch(() => { /* silent — footer keeps rendering */ });
    return () => { cancelled = true; };
  }, []);
  return (
  <footer className="bg-slate-900 border-t border-slate-700 py-12 px-6" data-testid="global-footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-8 mb-10">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold/80 mb-1">Prestigious Companies</p>
          <p className="font-heading text-lg font-bold text-white mb-3">Prestigious Hub</p>
          <p className="text-xs text-slate-400 leading-relaxed mb-3">
            The Financial Concierge Platform powering consumer funding, business funding, and credit improvement services.
          </p>
          <p className="text-xs text-slate-500 leading-relaxed">
            Prestigious Hub is part of the Prestigious Companies network of financial solutions.
          </p>
        </div>
        <FooterCol heading="Company"   items={COL_COMPANY}   testId="footer-col-company" />
        <FooterCol heading="Brands"    items={COL_SOLUTIONS} testId="footer-col-solutions" />
        <FooterCol heading="Legal"     items={COL_LEGAL}     testId="footer-col-legal" />
      </div>
      <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
        <div>
          <p className="text-sm text-slate-300 font-medium">© 2026 Prestigious Hub</p>
          <p className="text-xs text-slate-400 mt-1">
            A Prestigious Companies Network — Powering EasyQuickFund, Business Funding &amp; CreditForward360
          </p>
        </div>
        {/* v3.21-1803 · Permanent Install Hub App entry point.  Only
            renders when the runtime is not already an installed PWA.
            Native prompt used when browser offers one; otherwise
            platform-appropriate manual install instructions. */}
        <div className="flex items-center gap-3">
          <InstallHubAppButton />
          {/* v3.15-1171 — Version badge (mandatory).  v3.21-1788: value
              is now sourced dynamically from `/version.json` at mount so
              it never drifts from the running release. */}
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500" data-testid="footer-version-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-gold/80" />
            <span>{version || 'v—'}</span>
          </div>
        </div>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
